import {
  CATALOG_IMAGE_DOWNSCALE_FACTOR,
  CATALOG_IMAGE_MAX_DIMENSION_PX,
  CATALOG_IMAGE_MAX_DOWNSCALE_ATTEMPTS,
  CATALOG_IMAGE_MAX_SIZE_BYTES,
  CATALOG_IMAGE_MIME_TYPE,
  CATALOG_IMAGE_QUALITY_STEPS,
} from '@constants/constants';

const BYTES_PER_KILOBYTE = 1024;
const BYTES_PER_MEGABYTE = 1024 * 1024;
const BYTES_PER_GIGABYTE = 1024 * 1024 * 1024;

export function formatStorageBytes(bytes: number): string {
  if (bytes < BYTES_PER_KILOBYTE) {
    return `${bytes} B`;
  }

  if (bytes < BYTES_PER_MEGABYTE) {
    return `${(bytes / BYTES_PER_KILOBYTE).toFixed(1)} KB`;
  }

  if (bytes < BYTES_PER_GIGABYTE) {
    return `${(bytes / BYTES_PER_MEGABYTE).toFixed(1)} MB`;
  }

  return `${(bytes / BYTES_PER_GIGABYTE).toFixed(2)} GB`;
}

export function isOptimizedCatalogImage(file: File): boolean {
  return file.type === CATALOG_IMAGE_MIME_TYPE && file.size <= CATALOG_IMAGE_MAX_SIZE_BYTES;
}

/**
 * Converts an uploaded image into a catalog-ready WebP: scales the longest side down to
 * CATALOG_IMAGE_MAX_DIMENSION_PX and lowers the quality step by step until the result fits
 * in CATALOG_IMAGE_MAX_SIZE_BYTES. When every quality step is still too heavy, the image is
 * shrunk again by CATALOG_IMAGE_DOWNSCALE_FACTOR and retried.
 * WebP files that already fit are returned untouched to avoid re-encoding losses.
 * @param file - Image selected by the admin
 * @returns The optimized WebP file, or null when the browser cannot encode WebP or the image cannot reach the size limit
 */
export async function optimizeCatalogImage(file: File): Promise<File | null> {
  if (isOptimizedCatalogImage(file)) {
    return file;
  }

  const imageBitmap = await globalThis.createImageBitmap(file);

  try {
    const longestSide = Math.max(imageBitmap.width, imageBitmap.height);
    let scale = Math.min(1, CATALOG_IMAGE_MAX_DIMENSION_PX / longestSide);

    for (let downscaleAttempt = 0; downscaleAttempt <= CATALOG_IMAGE_MAX_DOWNSCALE_ATTEMPTS; downscaleAttempt++) {
      const canvas = drawScaledImage(imageBitmap, scale);

      for (const quality of CATALOG_IMAGE_QUALITY_STEPS) {
        const encodedImage = await encodeCanvas(canvas, quality);

        if (!encodedImage || encodedImage.type !== CATALOG_IMAGE_MIME_TYPE) {
          return null;
        }

        if (encodedImage.size <= CATALOG_IMAGE_MAX_SIZE_BYTES) {
          return new File([encodedImage], toWebpFileName(file.name), { type: CATALOG_IMAGE_MIME_TYPE });
        }
      }

      scale *= CATALOG_IMAGE_DOWNSCALE_FACTOR;
    }

    return null;
  } finally {
    imageBitmap.close();
  }
}

function drawScaledImage(imageBitmap: ImageBitmap, scale: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(imageBitmap.width * scale));
  canvas.height = Math.max(1, Math.round(imageBitmap.height * scale));
  canvas.getContext('2d')?.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

  return canvas;
}

function encodeCanvas(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, CATALOG_IMAGE_MIME_TYPE, quality));
}

function toWebpFileName(fileName: string): string {
  const baseName = fileName.replace(/\.[^.]+$/, '') || 'imagen';

  return `${baseName}.webp`;
}
